import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, X, Send, Bot, User, Sparkles, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AIChatWidgetProps {
  type?: 'public' | 'sales' | 'voice';
  title?: string;
  placeholder?: string;
  className?: string;
  isERP?: boolean;
}

export const AIChatWidget = ({ 
  type = 'public', 
  title = 'GPR AI Assistant',
  placeholder = 'Ask about plots, pricing, locations...',
  className,
  isERP = false
}: AIChatWidgetProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage: Message = {
        id: 'welcome',
        role: 'assistant',
        content: type === 'sales' 
          ? "Hello! I'm your AI Sales Assistant. I can help you with lead qualification, follow-up scripts, objection handling, and product knowledge. How can I assist you today?"
          : "Namaste! 🙏 I'm GPR Infra's AI assistant. I can help you with:\n\n• Plot availability & pricing\n• Project locations & amenities\n• Booking & EMI options\n• Site visit scheduling\n\nHow may I help you today?",
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, type]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            messages: [...messages.filter(m => m.id !== 'welcome'), userMessage].map(m => ({
              role: m.role,
              content: m.content,
            })),
            type,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();

      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data.content || "I apologize, but I couldn't process your request. Please try again or call us at 8297978080.",
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: "I'm having trouble connecting. Please try again or call us directly at 8297978080 for immediate assistance.",
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, messages, type]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed z-[60] rounded-full shadow-2xl",
          isERP 
            ? "bottom-6 right-6 h-12 w-12 bg-gradient-to-r from-royal-600 to-sky-500" 
            : "bottom-24 right-4 md:bottom-6 md:right-6 h-14 w-14 bg-gradient-to-r from-primary to-accent",
          "hover:scale-110 transition-all duration-300 animate-bounce-slow",
          className
        )}
        size="icon"
      >
        <MessageCircle className={cn("text-white", isERP ? "h-5 w-5" : "h-6 w-6")} />
        <span className="absolute -top-1 -right-1 h-4 w-4 bg-success rounded-full animate-pulse" />
      </Button>
    );
  }

  return (
    <Card className={cn(
      "fixed z-[70] shadow-2xl border-0 overflow-hidden",
      isERP 
        ? "bottom-6 right-6 w-96 h-[500px] rounded-2xl" 
        : "bottom-4 right-4 w-[90vw] max-w-[400px] h-[70vh] max-h-[600px] rounded-3xl",
      "flex flex-col animate-scale-in",
      className
    )}>
      {/* Header */}
      <div className={cn(
        "flex items-center justify-between p-4",
        isERP 
          ? "bg-gradient-to-r from-royal-600 to-sky-500" 
          : "bg-gradient-to-r from-primary via-primary to-accent"
      )}>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
            <Bot className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white flex items-center gap-2">
              {title}
              <Sparkles className="h-4 w-4 text-yellow-300" />
            </h3>
            <p className="text-xs text-white/70">Powered by AI</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 text-white hover:bg-white/20 hover:text-white rounded-full bg-white/10"
          onClick={() => setIsOpen(false)}
          title="Close chat"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4 bg-background" ref={scrollRef}>
        <div className="space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                "flex gap-3",
                message.role === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              {message.role === 'assistant' && (
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0">
                  <Bot className="h-4 w-4 text-white" />
                </div>
              )}
              <div
                className={cn(
                  "max-w-[80%] rounded-2xl px-4 py-3 text-sm",
                  message.role === 'user'
                    ? "bg-primary text-primary-foreground rounded-br-md"
                    : "bg-muted text-foreground rounded-bl-md"
                )}
              >
                <p className="whitespace-pre-wrap">{message.content}</p>
              </div>
              {message.role === 'user' && (
                <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
            </div>
          ))}
          {isLoading && (
            <div className="flex gap-3 justify-start">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Quick Actions */}
      {!isERP && messages.length <= 1 && (
        <div className="px-4 py-2 bg-muted/30 border-t border-border/50">
          <div className="flex flex-wrap gap-2">
            {['Plot Prices', 'Site Visit', 'EMI Plans', 'Locations'].map((action) => (
              <Badge
                key={action}
                variant="secondary"
                className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                onClick={() => {
                  setInput(action);
                  inputRef.current?.focus();
                }}
              >
                {action}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-border bg-background">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={isLoading}
            className="rounded-xl border-muted-foreground/20 focus:ring-primary"
          />
          <Button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            size="icon"
            className="rounded-xl bg-primary hover:bg-primary/90 flex-shrink-0"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        {!isERP && (
          <div className="mt-2 text-center">
            <a
              href="tel:8297978080"
              className="text-xs text-muted-foreground hover:text-primary flex items-center justify-center gap-1"
            >
              <Phone className="h-3 w-3" />
              Or call: 8297978080
            </a>
          </div>
        )}
      </div>
    </Card>
  );
};

export default AIChatWidget;
